// =============================================================================
// Origin UI — src/services/letterApi.ts
// API service for HR Letter Generation endpoints.
// =============================================================================

import { api } from './apiService';

// ─── Types ───

export interface LetterTemplateListItem {
  letterTemplateId: string;
  code: string;
  name: string;
  description: string | null;
  jurisdiction: string | null;
  includeSalary: boolean;
  compensationOnly: boolean;
  requiresAdditionalInput: boolean;
  sortOrder: number;
  isActive: boolean;
}

export interface LetterTemplateDetail extends LetterTemplateListItem {
  templateBody: string;
  additionalInputSchema: string | null;
  letterheadType: string;
  useUniversalFooter: boolean;
  includeStamp: boolean;
  signatoryDefault: string | null;
  signatoryTitleDefault: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface GenerateLetterRequest {
  additionalInputValues?: Record<string, string>;
  signatoryName?: string;
  signatoryTitle?: string;
}

export interface GeneratedLetterItem {
  generatedLetterId: string;
  letterTemplateId: string;
  templateCode: string;
  templateName: string;
  employeeId: string;
  employeeName: string;
  generatedByName: string;
  generatedAt: string;
  fileName: string;
  signatoryName: string;
  signatoryTitle: string;
  hasFile: boolean;
}

export interface AdditionalInputField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'date';
  required: boolean;
}

export interface OfficeDetail {
  officeDetailId: string;
  officeLocationId: number;
  officeName: string;
  legalEntityName: string;
  registeredAddress: string | null;
  phone: string | null;
  fax: string | null;
  website: string | null;
  footerOverride: string | null;
  letterheadType: string;
  stampBlobPath: string | null;
  businessRegistrationNo: string | null;
  defaultSignatory1Name: string | null;
  defaultSignatory1Title: string | null;
  defaultSignatory2Name: string | null;
  defaultSignatory2Title: string | null;
}

export interface ExchangeRate {
  exchangeRateId: string;
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  effectiveDate: string;
  isFixed: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

// ─── API ───

export const letterApi = {
  // Templates (employee-facing, filtered by jurisdiction + role)
  getAvailableTemplates: (employeeId: string) =>
    api.get<LetterTemplateListItem[]>(`/api/letters/templates/${employeeId}`),

  getTemplateDetail: (templateId: string) =>
    api.get<LetterTemplateDetail>(`/api/letters/templates/detail/${templateId}`),

  // Generation — format: 'pdf' (default) or 'docx'
  generateLetter: async (templateId: string, employeeId: string, request: GenerateLetterRequest, format: 'pdf' | 'docx' = 'pdf'): Promise<void> => {
    const token = await api.getToken();
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/letters/generate/${templateId}/${employeeId}?format=${format}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Generation failed: ${errorBody}`);
    }

    // Trigger file download
    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    let fileName = format === 'pdf' ? 'letter.pdf' : 'letter.docx';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match) fileName = match[1].replace(/['"]/g, '');
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  },

  // History
  getHistory: (employeeId: string) =>
    api.get<GeneratedLetterItem[]>(`/api/letters/history/${employeeId}`),

  downloadGeneratedLetter: async (employeeId: string, generatedLetterId: string, fileName: string): Promise<void> => {
    const token = await api.getToken();
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/letters/history/${employeeId}/${generatedLetterId}/download`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  },

  // Admin — templates
  getAllTemplates: () =>
    api.get<LetterTemplateListItem[]>('/api/admin/letter-templates'),

  getAdminTemplateDetail: (id: string) =>
    api.get<LetterTemplateDetail>(`/api/admin/letter-templates/${id}`),

  createTemplate: (data: Partial<LetterTemplateDetail>) =>
    api.post<LetterTemplateDetail>('/api/admin/letter-templates', data),

  updateTemplate: (id: string, data: Partial<LetterTemplateDetail>) =>
    api.put<LetterTemplateDetail>(`/api/admin/letter-templates/${id}`, data),

  toggleTemplate: (id: string) =>
    api.patch(`/api/admin/letter-templates/${id}/toggle`),

  // Admin — office details
  getAllOfficeDetails: () =>
    api.get<OfficeDetail[]>('/api/admin/office-details'),

  updateOfficeDetail: (officeLocationId: number, data: Partial<OfficeDetail>) =>
    api.put<OfficeDetail>(`/api/admin/office-details/${officeLocationId}`, data),

  // Admin — exchange rates
  getExchangeRates: () =>
    api.get<ExchangeRate[]>('/api/admin/exchange-rates'),

  createExchangeRate: (data: Partial<ExchangeRate>) =>
    api.post<ExchangeRate>('/api/admin/exchange-rates', data),

  updateExchangeRate: (id: string, data: Partial<ExchangeRate>) =>
    api.put<ExchangeRate>(`/api/admin/exchange-rates/${id}`, data),
};
